import Field from "../../../shared/ui/Field/Field";
import GeoAddressObjectAutocomplete from "./GeoAddressObjectAutocomplete";
import {
  BUILDING_NUMBER_MAX_LENGTH,
  LOCAL_AREA_MAX_LENGTH,
  MANUAL_ADDRESS_OBJECT_MAX_LENGTH,
  NAMED_OBJECT_MODE_MANUAL,
  applyAddressObjectSelection,
  applyBuildingNumberChange,
  applyLocalAreaChange,
  applyManualAddressObjectChange,
  clearLegacyAddressFact,
  hasInactiveLinkedAddressObject,
  hasUnresolvedLegacyAddress,
  switchToCanonicalNamedObject,
  switchToManualNamedObject,
} from "../lib/locationGeo";

function LocationStructuredAddressFields({
  values,
  onChange,
  fieldErrors = {},
  idPrefix = "loc",
}) {
  const settlementCode = values.selectedSettlement?.code || "";
  const settlementReady = Boolean(settlementCode);
  const isManual = values.namedObjectMode === NAMED_OBJECT_MODE_MANUAL;
  const unresolved = hasUnresolvedLegacyAddress(values);
  const inactiveLinked = hasInactiveLinkedAddressObject(values);

  const handleLocalAreaChange = (event) => {
    onChange(applyLocalAreaChange(values, event.target.value));
  };

  const handleAddressObjectSelect = (addressObject) => {
    onChange(applyAddressObjectSelection(values, addressObject));
  };

  const handleManualChange = (event) => {
    onChange(applyManualAddressObjectChange(values, event.target.value));
  };

  const handleBuildingChange = (event) => {
    onChange(applyBuildingNumberChange(values, event.target.value));
  };

  const handleSwitchToManual = () => {
    onChange(switchToManualNamedObject(values));
  };

  const handleSwitchToCanonical = () => {
    onChange(switchToCanonicalNamedObject(values));
  };

  const handleClearLegacy = () => {
    onChange(clearLegacyAddressFact(values));
  };

  return (
    <>
      {unresolved ? (
        <div
          className="geo-legacy-address-notice"
          role="status"
          data-testid="geo-legacy-address-notice"
        >
          <p className="geo-legacy-address-notice__title">
            Збережена адреса ще не перенесена в структуровані поля
          </p>
          <p className="geo-legacy-address-notice__value">
            {values.legacyUnresolvedAddress}
          </p>
          <p className="form-hint">
            Не розбираємо цей рядок автоматично. Оберіть об’єкт з довідника,
            введіть назву вручну або очистіть стару адресу.
          </p>
          <button
            type="button"
            className="geo-named-object-fallback"
            onClick={handleClearLegacy}
          >
            Очистити стару адресу
          </button>
          {fieldErrors.legacy_address ? (
            <small className="form-hint text-danger">
              {fieldErrors.legacy_address}
            </small>
          ) : null}
        </div>
      ) : null}

      <div
        className="ap-row ap-row--2 geo-street-building-row"
        data-testid="geo-street-building-row"
      >
        {isManual ? (
          <div className="ap-field geo-address-field">
            <Field
              id={`${idPrefix}-manual-address-object`}
              label="Вулиця або адресний об’єкт (вручну)"
              help="Назву не буде додано до довідника адресних об’єктів."
              error={fieldErrors.manual_address_object}
              className="geo-canonical-field__field"
            >
              <input
                id={`${idPrefix}-manual-address-object`}
                name="manual_address_object"
                type="text"
                className="form-control"
                maxLength={MANUAL_ADDRESS_OBJECT_MAX_LENGTH}
                placeholder="Напр.: вул. Незалежності"
                value={values.manual_address_object || ""}
                onChange={handleManualChange}
                disabled={!settlementReady}
              />
            </Field>
            <button
              type="button"
              className="geo-named-object-fallback"
              onClick={handleSwitchToCanonical}
              disabled={!settlementReady}
            >
              Обрати з довідника
            </button>
          </div>
        ) : (
          <div className="ap-field geo-address-field">
            <GeoAddressObjectAutocomplete
              id={`${idPrefix}-address-object`}
              settlementCode={settlementCode}
              enabled={settlementReady}
              selectedAddressObject={values.address_object}
              onSelect={handleAddressObjectSelect}
              error={fieldErrors.address_object_id}
              disabled={!settlementReady}
            />
            {inactiveLinked ? (
              <small
                className="form-hint text-warning"
                data-testid="geo-inactive-address-object"
              >
                Поточний об’єкт довідника неактивний. Перед збереженням
                оберіть активний об’єкт або введіть назву вручну.
              </small>
            ) : null}
            <button
              type="button"
              className="geo-named-object-fallback"
              onClick={handleSwitchToManual}
              disabled={!settlementReady}
            >
              Не знайшли у довіднику? Ввести вручну
            </button>
          </div>
        )}

        <div className="ap-field geo-building-number-field geo-canonical-field">
          <Field
            id={`${idPrefix}-building-number`}
            label="Номер будинку (необов’язково)"
            help="Напр.: 61А"
            error={fieldErrors.building_number}
            className="geo-canonical-field__field"
          >
            <input
              id={`${idPrefix}-building-number`}
              name="building_number"
              type="text"
              className="form-control"
              maxLength={BUILDING_NUMBER_MAX_LENGTH}
              value={values.building_number || ""}
              onChange={handleBuildingChange}
              disabled={!settlementReady}
            />
          </Field>
        </div>
      </div>

      <div className="ap-row ap-row--2">
        <div className="ap-field geo-local-area-field">
          <label htmlFor={`${idPrefix}-local-area`}>
            Місцевість / район міста (необов’язково)
          </label>
          <input
            id={`${idPrefix}-local-area`}
            name="local_area"
            type="text"
            className="form-control"
            maxLength={LOCAL_AREA_MAX_LENGTH}
            placeholder="Напр.: Сихів, Печерськ"
            value={values.local_area || ""}
            onChange={handleLocalAreaChange}
          />
          <small className="form-hint">
            Не адміністративний район або громада КАТОТТГ.
            Наприклад: мікрорайон, житловий масив або місцева назва району.
          </small>
          {fieldErrors.local_area ? (
            <small className="form-hint text-danger">
              {fieldErrors.local_area}
            </small>
          ) : null}
        </div>
      </div>
    </>
  );
}

export default LocationStructuredAddressFields;
